class Player {
  // indicator that integration didn't provide a number for this player
  constructor(name, number, captain, starting, teamOfficial, role, integrationId, missingRealNumber) {
    this.name = name;
    this.number = number;
    this.captain = captain;
    this.starting = starting;
    this.teamOfficial = teamOfficial;
    this.role = role;
    this.integrationId = integrationId;
    this.missingRealNumber = missingRealNumber;
  }
  static fromJSON(json) {
    return new Player(
      json.name,
      json.number,
      json.captain,
      json.starting,
      json.teamOfficial,
      json.role,
      json.integrationId,
      json.missingRealNumber
    );
  }
}
class Team {
  constructor(teamName, shortName, players, side, color) {
    this.teamName = teamName;
    this.shortName = shortName;
    this.players = players;
    this.side = side;
    this.color = color;
  }
  static fromJSON(json) {
    let players = json.players.map(function(player) {
      return Player.fromJSON(player);
    });
    return new Team(
      json.teamName,
      json.shortName,
      players,
      json.side,
      json.color
    );
  }
}
class Half {
  constructor(name, playing, length, isExtraTime) {
    this.name = name;
    this.playing = playing;
    this.length = length;
    this.isExtraTime = isExtraTime;
  }
  static fromJSON(json) {
    return new Half(json.name, json.playing, json.length, json.isExtraTime);
  }
}
class Reason {
  constructor(code, reason, type, secondYellow, sinBin, subCodes, teamOfficial) {
    this.code = code;
    this.reason = reason;
    this.type = type;
    this.secondYellow = secondYellow;
    this.sinBin = sinBin;
    this.subCodes = subCodes;
    this.teamOfficial = teamOfficial;
  }
  static fromJSON(json) {
    let subCodes = json.subCodes && json.subCodes.map(function(reason) {
      return Reason.fromJSON(reason);
    });
    return new Reason(
      json.code,
      json.reason,
      json.type,
      json.secondYellow,
      json.sinBin,
      subCodes,
      json.teamOfficial
    );
  }
}
var EventName = /* @__PURE__ */ ((EventName2) => {
  EventName2["goal"] = "Goal";
  EventName2["half"] = "Half";
  EventName2["incident"] = "Incident";
  EventName2["penalties"] = "Penalties";
  EventName2["penaltyShot"] = "PenaltyShot";
  EventName2["substitution"] = "Substitution";
  EventName2["abandoned"] = "Abandoned";
  return EventName2;
})(EventName || {});
class MatchEvent {
  constructor(timestamp, eventName, half, minuteOfPlay, additionalTime) {
    this.timestamp = timestamp;
    this.eventName = eventName;
    this.half = half;
    this.minuteOfPlay = minuteOfPlay;
    this.additionalTime = additionalTime;
  }
}
class GoalEvent extends MatchEvent {
  constructor(timestamp, eventName, half, minuteOfPlay, team, player, ownGoal, penalty, freeKick) {
    super(timestamp, eventName, half, minuteOfPlay);
    this.team = team;
    this.player = player;
    this.ownGoal = ownGoal;
    this.penalty = penalty;
    this.freeKick = freeKick;
  }
  static fromJSON(json) {
    return new GoalEvent(
      json.timestamp,
      json.eventName,
      json.half,
      json.minuteOfPlay,
      json.team,
      json.player,
      json.ownGoal,
      json.penalty,
      json.freeKick
    );
  }
}
class SubstitutionEvent extends MatchEvent {
  constructor(timestamp, eventName, half, minuteOfPlay, team, playerOff, playerOn) {
    super(timestamp, eventName, half, minuteOfPlay);
    this.team = team;
    this.playerOff = playerOff;
    this.playerOn = playerOn;
  }
  static fromJSON(json) {
    return new SubstitutionEvent(
      json.timestamp,
      json.eventName,
      json.half,
      json.minuteOfPlay,
      json.team,
      json.playerOff,
      json.playerOn
    );
  }
}
class PenaltyShotEvent extends MatchEvent {
  constructor(timestamp, eventName, half, minuteOfPlay, team, player, goal) {
    super(timestamp, eventName, half, minuteOfPlay);
    this.team = team;
    this.player = player;
    this.goal = goal;
  }
  static fromJSON(json) {
    return new PenaltyShotEvent(
      json.timestamp,
      json.eventName,
      json.half,
      json.minuteOfPlay,
      json.team,
      json.player,
      json.goal
    );
  }
}
class PenaltiesEvent extends MatchEvent {
  constructor(timestamp, eventName, half, minuteOfPlay, endTime) {
    super(timestamp, eventName, half, minuteOfPlay);
    this.endTime = endTime;
  }
  static fromJSON(json) {
    return new PenaltiesEvent(
      json.timestamp,
      json.eventName,
      json.half,
      json.minuteOfPlay,
      json.endTime
    );
  }
}
class HalfEvent extends MatchEvent {
  // whether this half object represents extra time
  constructor(timestamp, eventName, half, minuteOfPlay, index, name, playing, length, kickOffSide, injuryTimeAccumulated, injuryTimeInInjuryTimeAccumulated, endMinute, endTime, isExtraTime) {
    super(timestamp, eventName, half, minuteOfPlay);
    this.index = index;
    this.name = name;
    this.playing = playing;
    this.length = length;
    this.kickOffSide = kickOffSide;
    this.injuryTimeAccumulated = injuryTimeAccumulated;
    this.injuryTimeInInjuryTimeAccumulated = injuryTimeInInjuryTimeAccumulated;
    this.endMinute = endMinute;
    this.endTime = endTime;
    this.isExtraTime = isExtraTime;
  }
  static fromJSON(json) {
    return new HalfEvent(
      json.timestamp,
      json.eventName,
      json.half,
      json.minuteOfPlay,
      json.index,
      json.name,
      json.playing,
      json.length,
      json.kickOffSide,
      json.injuryTimeAccumulated,
      json.injuryTimeInInjuryTimeAccumulated,
      json.endMinute,
      json.endTime,
      json.isExtraTime
    );
  }
}
class IncidentEvent extends MatchEvent {
  constructor(timestamp, eventName, half, minuteOfPlay, team, player, reason, card, duringSinBin, sinBin, playerEntersSinBinTimestamp, playerLeavesSinBinTimestamp, sinBinInjuryTimeAccumulated) {
    super(timestamp, eventName, half, minuteOfPlay);
    this.team = team;
    this.player = player;
    this.reason = reason;
    this.card = card;
    this.duringSinBin = duringSinBin;
    this.sinBin = sinBin;
    this.playerEntersSinBinTimestamp = playerEntersSinBinTimestamp;
    this.playerLeavesSinBinTimestamp = playerLeavesSinBinTimestamp;
    this.sinBinInjuryTimeAccumulated = sinBinInjuryTimeAccumulated;
  }
  static fromJSON(json) {
    return new IncidentEvent(
      json.timestamp,
      json.eventName,
      json.half,
      json.minuteOfPlay,
      json.team,
      json.player,
      json.reason,
      json.card,
      json.duringSinBin,
      json.sinBin,
      json.playerEntersSinBinTimestamp,
      json.playerLeavesSinBinTimestamp,
      json.sinBinInjuryTimeAccumulated
    );
  }
}
class Match {
  constructor(matchId, teams, halves, matchEvents, currentHalf, kickOffTime, extraTimeAvailable, extraTimeHalfLength, penaltiesAvailable, matchFinished, sinBinSystem, sinBinTimerLength, customMisconductCodes, misconductCodeId, matchCreatedTs, matchFinishedTs, device, matchAbandoned, officialRole) {
    this.matchEvents = {};
    this.matchId = matchId;
    this.teams = teams;
    this.halves = halves;
    this.matchEvents = matchEvents;
    this.currentHalf = currentHalf;
    this.kickOffTime = kickOffTime;
    this.extraTimeAvailable = extraTimeAvailable;
    this.extraTimeHalfLength = extraTimeHalfLength;
    this.penaltiesAvailable = penaltiesAvailable;
    this.matchFinished = matchFinished;
    this.sinBinSystem = sinBinSystem;
    this.sinBinTimerLength = sinBinTimerLength;
    this.customMisconductCodes = customMisconductCodes;
    this.misconductCodeId = misconductCodeId;
    this.matchCreatedTs = matchCreatedTs;
    this.matchFinishedTs = matchFinishedTs;
    this.device = device;
    this.matchAbandoned = matchAbandoned;
    this.officialRole = officialRole;
  }
  static fromJSON(json) {
    let newEvents = {};
    json.matchEvents && Object.keys(json.matchEvents).map(function(key) {
      switch (json.matchEvents[key].eventName) {
        case EventName.goal:
          newEvents[key] = GoalEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.substitution:
          newEvents[key] = SubstitutionEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.penaltyShot:
          newEvents[key] = PenaltyShotEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.penalties:
          newEvents[key] = PenaltiesEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.half:
          newEvents[key] = HalfEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.incident:
          newEvents[key] = IncidentEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
      }
    });
    let teams = json.teams && json.teams.map(function(team) {
      return Team.fromJSON(team);
    });
    let halves = json.halves && json.halves.map(function(half) {
      return Half.fromJSON(half);
    });
    let customCodes = json.customMisconductCodes && json.customMisconductCodes.map(function(reason) {
      return Reason.fromJSON(reason);
    });
    const match = new Match(
      json.matchId,
      teams,
      halves,
      newEvents,
      json.currentHalf,
      json.kickOffTime,
      json.extraTimeAvailable,
      json.extraTimeHalfLength,
      json.penaltiesAvailable,
      json.matchFinished,
      json.sinBinSystem,
      json.sinBinTimerLength,
      customCodes,
      json.misconductCodeId,
      json.matchCreatedTs,
      json.matchFinishedTs,
      json.device,
      json.matchAbandoned,
      json.officialRole
    );
    match.subOpportunities = json.subOpportunities ?? false;
    match.subOpportunitiesCount = json.subOpportunitiesCount;
    match.subOpportunitiesInETCount = json.subOpportunitiesInETCount;
    return match;
  }
}
class TimerLayout {
  // constructor(map: any) {
  //     this.main = map.main;
  //     this.secondary = map.secondary;
  //     this.left = map.left;
  //     this.right = map.right;
  // }
  constructor(main, secondary, left, right) {
    this.main = main;
    this.secondary = secondary;
    this.left = left;
    this.right = right;
  }
  isValid() {
    let hasOnlySecondary = this.secondary != null && this.left == null && this.right == null;
    let hasOnlyLeftAndRight = this.secondary == null && this.left != null && this.right != null;
    return this.main && (hasOnlySecondary || hasOnlyLeftAndRight);
  }
  static fromJSON(json) {
    return new TimerLayout(
      json.main,
      json.secondary,
      json.left,
      json.right
    );
  }
}
var WatchFace = /* @__PURE__ */ ((WatchFace2) => {
  WatchFace2["classic"] = "classic";
  WatchFace2["bigTimers"] = "bigTimers";
  return WatchFace2;
})(WatchFace || {});
var TimerType = /* @__PURE__ */ ((TimerType2) => {
  TimerType2["elapsed"] = "elapsed";
  TimerType2["additional"] = "additional";
  TimerType2["countdown"] = "countdown";
  TimerType2["countup"] = "countup";
  return TimerType2;
})(TimerType || {});
class Config {
  // disable half-end vibrations and alerts on watch
  constructor(layout, recordGoalScorers, automaticallyBeginHalfTime, elapsedHalfTimerFromZero, homeTeamPlayerNumberPicker, awayTeamPlayerNumberPicker, disableGPS, showGoalOptions, watchFace, misconductCodes, screenAlwaysOn, disableWebPairing, showTimeOfDayOnKO, disableTimerAlerts) {
    this.disableWebPairing = false;
    this.disableTimerAlerts = false;
    this.layout = layout;
    this.recordGoalScorers = recordGoalScorers;
    this.automaticallyBeginHalfTime = automaticallyBeginHalfTime;
    this.elapsedHalfTimerFromZero = elapsedHalfTimerFromZero;
    this.homeTeamPlayerNumberPicker = homeTeamPlayerNumberPicker;
    this.awayTeamPlayerNumberPicker = awayTeamPlayerNumberPicker;
    this.playerNumberPicker = homeTeamPlayerNumberPicker || awayTeamPlayerNumberPicker;
    this.disableGPS = disableGPS;
    this.showGoalOptions = showGoalOptions;
    this.watchFace = watchFace;
    this.misconductCodes = misconductCodes;
    this.screenAlwaysOn = screenAlwaysOn;
    this.disableWebPairing = disableWebPairing;
    this.showTimeOfDayOnKO = showTimeOfDayOnKO;
    this.disableTimerAlerts = disableTimerAlerts;
  }
  static fromJSON(json) {
    let timerLayout = TimerLayout.fromJSON(json.layout);
    let misconductCodes = json.misconductCodes && json.misconductCodes.map(function(reason) {
      return Reason.fromJSON(reason);
    });
    return new Config(
      timerLayout,
      json.recordGoalScorers,
      json.automaticallyBeginHalfTime,
      json.elapsedHalfTimerFromZero,
      json.homeTeamPlayerNumberPicker,
      json.awayTeamPlayerNumberPicker,
      json.disableGPS,
      json.showGoalOptions,
      json.watchFace,
      misconductCodes,
      json.screenAlwaysOn,
      json.disableWebPairing,
      json.showTimeOfDayOnKO,
      json.disableTimerAlerts
    );
  }
  static defaultConfig() {
    const layout = new TimerLayout(
      TimerType.elapsed,
      TimerType.additional,
      void 0,
      void 0
    );
    return new Config(
      layout,
      false,
      true,
      true,
      false,
      false,
      false,
      false,
      WatchFace.classic,
      [],
      true,
      false,
      false,
      // showTimeOfDayOnKO - default OFF
      false
      // disableTimerAlerts - default OFF
    );
  }
}
var PlayerCardStatus = /* @__PURE__ */ ((PlayerCardStatus2) => {
  PlayerCardStatus2["none"] = "none";
  PlayerCardStatus2["red"] = "red";
  PlayerCardStatus2["yellow"] = "yellow";
  return PlayerCardStatus2;
})(PlayerCardStatus || {});
var SinBinSystem = /* @__PURE__ */ ((SinBinSystem2) => {
  SinBinSystem2["none"] = "none";
  SinBinSystem2["systemA"] = "systemA";
  SinBinSystem2["systemB"] = "systemB";
  SinBinSystem2["systemB2024"] = "systemB2024";
  return SinBinSystem2;
})(SinBinSystem || {});
class CardStatus {
  constructor(type, count, sinBinCount, currentlySinBinned, permanentlySinBinned, blockSubstitution) {
    this.type = type;
    this._count = count || 0;
    this._sinBinCount = sinBinCount || 0;
    this._currentlySinBinned = currentlySinBinned || false;
    this._permanentlySinBinned = permanentlySinBinned || false;
    this._blockSubstitution = blockSubstitution || false;
  }
  count() {
    switch (this.type) {
      case PlayerCardStatus.none:
      case PlayerCardStatus.red:
        return 0;
      case PlayerCardStatus.yellow:
        return this._count;
    }
  }
  sinBinCount() {
    switch (this.type) {
      case PlayerCardStatus.none:
      case PlayerCardStatus.red:
        return 0;
      case PlayerCardStatus.yellow:
        return this._sinBinCount;
    }
  }
  blockSubstitution(sinBinSystem) {
    switch (this.type) {
      case PlayerCardStatus.none:
        return false;
      case PlayerCardStatus.red:
        return true;
      case PlayerCardStatus.yellow:
        return this._blockSubstitution || this.isSystemBWithCautionAndSinBins(sinBinSystem);
    }
  }
  currentlySinBinned() {
    switch (this.type) {
      case PlayerCardStatus.none:
      case PlayerCardStatus.red:
        return false;
      case PlayerCardStatus.yellow:
        return this._currentlySinBinned;
    }
  }
  permanentlySinBinned(sinBinSystem) {
    switch (this.type) {
      case PlayerCardStatus.none:
      case PlayerCardStatus.red:
        return false;
      case PlayerCardStatus.yellow:
        return this._permanentlySinBinned || this._sinBinCount > 1 && !this._currentlySinBinned || this.isSystemBWithCautionAndSinBins(sinBinSystem);
    }
  }
  isSystemBWithCautionAndSinBins(sinBinSystem) {
    return sinBinSystem === SinBinSystem.systemB && this._sinBinCount > 1 && this.hasCaution();
  }
  hasCaution() {
    switch (this.type) {
      case PlayerCardStatus.yellow:
        return this._count > this._sinBinCount;
      default:
        return false;
    }
  }
}
class FlashMessage {
  constructor(title, line1, line2, line3, img, imgLabel, classColours, autoHide = true) {
    this.title = title;
    this.line1 = line1;
    this.line2 = line2;
    this.line3 = line3;
    this.img = img;
    this.imgLabel = imgLabel;
    this.classColours = classColours;
    this.autoHide = autoHide;
  }
}
class InjuryTimeUpdate {
  constructor(halfIndex, injuryTimeAccumulated, injuryTimeInInjuryTimeAccumulated, injuryTime) {
    this.halfIndex = halfIndex;
    this.injuryTimeAccumulated = injuryTimeAccumulated;
    this.injuryTimeInInjuryTimeAccumulated = injuryTimeInInjuryTimeAccumulated;
    this.injuryTime = injuryTime;
  }
}
class Timings {
  constructor(period1, period2, interval1, period3, period4, interval2, interval3, extraTimeHalfLength, sinBinTimerLength) {
    this.period1 = period1;
    this.period2 = period2;
    this.period3 = period3;
    this.period4 = period4;
    this.interval1 = interval1;
    this.interval2 = interval2;
    this.interval3 = interval3;
    this.extraTimeHalfLength = extraTimeHalfLength;
    this.sinBinTimerLength = sinBinTimerLength;
  }
  static fromJSON(json) {
    return new Timings(
      json.period1,
      json.period2,
      json.interval1,
      json.period3,
      json.period4,
      json.interval2,
      json.interval3,
      json.extraTimeHalfLength,
      json.sinBinTimerLength
    );
  }
}
class TemplateConfig {
  constructor(timings, periodsNo, withGoalScorers, teamSize, subsNo, subOpportunities, subOpportunitiesCount, subOpportunitiesInETCount, extraTimeAvailable, penaltiesAvailable, sinBinSystem = SinBinSystem.none, misconductCodeId) {
    this.timings = timings;
    this.periodsNo = periodsNo;
    this.withGoalScorers = withGoalScorers;
    this.teamSize = teamSize;
    this.subsNo = subsNo;
    this.subOpportunities = subOpportunities;
    this.subOpportunitiesCount = subOpportunitiesCount;
    this.subOpportunitiesInETCount = subOpportunitiesInETCount;
    this.extraTimeAvailable = extraTimeAvailable;
    this.penaltiesAvailable = penaltiesAvailable;
    this.sinBinSystem = sinBinSystem;
    this.misconductCodeId = misconductCodeId;
  }
  static fromJSON(json) {
    return new TemplateConfig(
      json.timings,
      json.periodsNo,
      json.withGoalScorers,
      json.teamSize,
      json.subsNo,
      json.subOpportunities ?? false,
      json.subOpportunitiesCount,
      json.subOpportunitiesInETCount,
      json.extraTimeAvailable,
      json.penaltiesAvailable,
      json.sinBinSystem,
      json.misconductCodeId
    );
  }
  static fromMatch(match) {
    var _a;
    let halves = match.halves ?? [];
    if (halves.length === 0) {
      throw new Error(
        "Cannot derive a template from a match with no halves"
      );
    }
    let regularHalves = halves.filter((half) => !half.isExtraTime);
    let extraTimeHalves = halves.filter((half) => half.isExtraTime);
    let periods = regularHalves.length > 0 ? regularHalves : halves;
    let matchPeriod1 = periods[0].length / 6e4;
    let matchPeriod2 = periods.length >= 3 ? periods[2].length / 6e4 : void 0;
    let matchPeriod3 = periods.length >= 5 ? periods[4].length / 6e4 : void 0;
    let matchPeriod4 = periods.length >= 7 ? periods[6].length / 6e4 : void 0;
    let matchInterval1 = periods.length >= 3 ? periods[1].length / 6e4 : void 0;
    let matchInterval2 = periods.length >= 5 ? periods[3].length / 6e4 : void 0;
    let matchInterval3 = periods.length >= 7 ? periods[5].length / 6e4 : void 0;
    let extraTimeHalfLengthMs = match.extraTimeHalfLength ?? ((_a = extraTimeHalves[0]) == null ? void 0 : _a.length);
    let timings = new Timings(
      matchPeriod1,
      matchPeriod2,
      matchInterval1,
      matchPeriod3,
      matchPeriod4,
      matchInterval2,
      matchInterval3,
      extraTimeHalfLengthMs ? extraTimeHalfLengthMs / 6e4 : match.extraTimeAvailable ? 15 : void 0,
      match.sinBinSystem === "none" ? void 0 : 10
    );
    let periodsNo = 2;
    if (periods.length === 1) {
      periodsNo = 1;
    } else if (periods.length === 3) {
      periodsNo = 2;
    } else if (periods.length === 5) {
      periodsNo = 3;
    } else if (periods.length === 7) {
      periodsNo = 4;
    }
    let sinBinSystem = match.sinBinSystem || SinBinSystem.none;
    return new TemplateConfig(
      timings,
      "" + periodsNo,
      false,
      11,
      5,
      match.subOpportunities ?? false,
      match.subOpportunitiesCount,
      match.subOpportunitiesInETCount,
      match.extraTimeAvailable,
      match.penaltiesAvailable,
      sinBinSystem,
      match.misconductCodeId || "fifa"
    );
  }
}
class MatchData {
  constructor(match, config, template) {
    this.match = match;
    this.config = config;
    this.template = template;
  }
  static fromJSON(json) {
    const template = json.template ? TemplateConfig.fromJSON(json.template) : void 0;
    return new MatchData(
      Match.fromJSON(json.match),
      Config.fromJSON(json.config),
      template
    );
  }
}
class MatchOfficials {
  constructor(referee, assistant1, assistant2, fourthOfficial, observer) {
    this.referee = referee;
    this.assistant1 = assistant1;
    this.assistant2 = assistant2;
    this.fourthOfficial = fourthOfficial;
    this.observer = observer;
  }
  static fromJSON(json) {
    return new MatchOfficials(
      json.referee,
      json.assistant1,
      json.assistant2,
      json.fourthOfficial,
      json.observer
    );
  }
}
class Integration {
  constructor(partner, homeTeamId, awayTeamId, poolId, matchId, participantId, disconnected = false) {
    this.partner = partner;
    this.homeTeamId = homeTeamId;
    this.awayTeamId = awayTeamId;
    this.poolId = poolId;
    this.matchId = matchId;
    this.participantId = participantId;
    this.disconnected = disconnected;
  }
  static fromJSON(json) {
    return new Integration(
      json.partner,
      json.homeTeamId,
      json.awayTeamId,
      json.poolId,
      json.matchId,
      json.participantId,
      json.disconnected
    );
  }
}
class Wellbeing {
  constructor(score, comments) {
    this.score = score;
    this.comments = comments;
  }
  static fromJSON(json) {
    return new Wellbeing(json.score, json.comments);
  }
}
class AbandonEvent extends MatchEvent {
  constructor(timestamp, eventName, half, minuteOfPlay, name) {
    super(timestamp, eventName, half, minuteOfPlay);
    this.name = name;
  }
  static fromJSON(json) {
    return new AbandonEvent(
      json.timestamp,
      json.eventName,
      json.half,
      json.minuteOfPlay,
      json.name
    );
  }
}
class MatchPhone {
  constructor(id, rev, refsixUpdateCount, refsixCount, date, officialRole, competition, venue, homeTeam, homeTeamShort, homeColor, awayTeam, awayTeamShort, awayColor, timings, periodsNo, withGoalScorers, sinBinSystem, misconductCodeId, teamSize, subsNo, players, matchFinished, eventsProcessed, matchEvents, stats, results, device, appVersion, extraTimeAvailable, penaltiesAvailable, playedExtraTime, playedPenalties, hasTracking, hasHeartRate, earnings, notes, matchOfficials, integration, keywords, assessment, wellbeing, customMisconductCodes, timezone, matchAbandoned, teamSheetRequests, pendingAcceptance, matchInProgress, source, playerOfTheMatch, teamBehavior, subOpportunities, subOpportunitiesCount, subOpportunitiesInETCount) {
    this.matchFinished = false;
    this.matchInProgress = false;
    this.eventsProcessed = false;
    this.matchEvents = {};
    this.device = { model: "", os: { name: "" } };
    this._id = id;
    this._rev = rev;
    this.refsixUpdateCount = refsixUpdateCount;
    this.refsixCount = refsixCount;
    this.date = date;
    this.officialRole = officialRole;
    this.competition = competition;
    this.venue = venue;
    this.homeTeam = homeTeam;
    this.homeTeamShort = homeTeamShort;
    this.homeColor = homeColor;
    this.awayTeam = awayTeam;
    this.awayTeamShort = awayTeamShort;
    this.awayColor = awayColor;
    this.timings = timings;
    this.periodsNo = periodsNo;
    this.withGoalScorers = withGoalScorers;
    this.sinBinSystem = sinBinSystem;
    this.misconductCodeId = misconductCodeId;
    this.teamSize = teamSize;
    this.subsNo = subsNo;
    this.subOpportunities = subOpportunities;
    this.subOpportunitiesCount = subOpportunitiesCount;
    this.subOpportunitiesInETCount = subOpportunitiesInETCount;
    this.players = players;
    this.matchFinished = matchFinished;
    this.eventsProcessed = eventsProcessed;
    this.matchEvents = matchEvents;
    this.stats = stats;
    this.results = results;
    this.device = device;
    this.appVersion = appVersion;
    this.extraTimeAvailable = extraTimeAvailable;
    this.penaltiesAvailable = penaltiesAvailable;
    this.playedExtraTime = playedExtraTime;
    this.playedPenalties = playedPenalties;
    this.hasTracking = hasTracking;
    this.hasHeartRate = hasHeartRate;
    this.earnings = earnings;
    this.notes = notes;
    this.matchOfficials = matchOfficials;
    this.integration = integration;
    this.keywords = keywords;
    this.assessment = assessment;
    this.wellbeing = wellbeing;
    this.customMisconductCodes = customMisconductCodes;
    this.timezone = timezone;
    this.teamSheetRequests = teamSheetRequests;
    this.pendingAcceptance = pendingAcceptance;
    this.matchInProgress = matchInProgress;
    this.matchAbandoned = matchAbandoned;
    this.source = source;
    this.playerOfTheMatch = playerOfTheMatch;
    this.teamBehavior = teamBehavior;
  }
  static fromJSON(json) {
    let newEvents = {};
    json.matchEvents && Object.keys(json.matchEvents).map(function(key) {
      switch (json.matchEvents[key].eventName) {
        case EventName.goal:
          newEvents[key] = GoalEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.substitution:
          newEvents[key] = SubstitutionEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.penaltyShot:
          newEvents[key] = PenaltyShotEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.penalties:
          newEvents[key] = PenaltiesEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.half:
          newEvents[key] = HalfEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.incident:
          newEvents[key] = IncidentEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
        case EventName.abandoned:
          newEvents[key] = AbandonEvent.fromJSON(
            json.matchEvents[key]
          );
          break;
      }
    });
    let newPlayers = {};
    json.players && Object.keys(json.players).map(function(team) {
      newPlayers[team] = json.players[team].map(function(player) {
        return Player.fromJSON(player);
      });
    });
    let timings = json.timings ? Timings.fromJSON(json.timings) : void 0;
    let matchOfficials = json.matchOfficials ? MatchOfficials.fromJSON(json.matchOfficials) : void 0;
    let integration = json.integration ? Integration.fromJSON(json.integration) : void 0;
    let wellbeing = json.wellbeing ? Wellbeing.fromJSON(json.wellbeing) : void 0;
    let customCodes = void 0;
    if (Array.isArray(json.customMisconductCodes)) {
      customCodes = json.customMisconductCodes.map(function(reasonJson) {
        return Reason.fromJSON(reasonJson);
      });
    }
    return {
      ...json,
      timings,
      players: newPlayers,
      matchEvents: newEvents,
      matchOfficials,
      integration,
      wellbeing,
      customMisconductCodes: customCodes,
      subOpportunities: json.subOpportunities ?? false,
      subOpportunitiesCount: json.subOpportunitiesCount ?? void 0,
      subOpportunitiesInETCount: json.subOpportunitiesInETCount ?? void 0
    };
  }
}
class PenaltiesScore {
  constructor() {
    this.scored = 0;
    this.outOf = 0;
  }
}
class PlayerOfTheMatch {
  constructor(teamSide, player) {
    this.teamSide = teamSide;
    this.player = player;
  }
  static fromJSON(json) {
    return new PlayerOfTheMatch(json.teamSide, json.player);
  }
}
class TeamBehavior {
  constructor(teamBehaviorScore) {
    this.teamBehaviorScore = teamBehaviorScore;
  }
  static fromJSON(json) {
    return new TeamBehavior(json.teamBehaviorScore);
  }
}
class SinBinTimer {
  constructor(player, playerSide, sinBinLength, accumulatedTime, startedAt, timer) {
    this.player = player;
    this.playerSide = playerSide;
    this.sinBinLength = sinBinLength;
    this.accumulatedTime = accumulatedTime;
    this.startedAt = startedAt;
    this.timer = timer;
  }
}
class StatsLegacy {
  constructor() {
    this.yellow = 0;
    this.red = 0;
    this.sinBin = 0;
    this.penalties = 0;
    this.goals = 0;
  }
}
const CURRENT_STATS_VERSION = 18;
class Stats extends StatsLegacy {
  constructor() {
    super();
    this.version = CURRENT_STATS_VERSION;
    this.gpsCalibratedTotal = 0;
    this.minutesPlayed = 0;
    this.minutesPlayedAsReferee = 0;
    this.minutesPlayedAsAssistant = 0;
    this.minutesPlayedAsFourthOfficial = 0;
    this.minutesPlayedAsObserver = 0;
    this.speedMax = 0;
    this.speedAverage = 0;
    this.goalsTotal = 0;
    this.goalsHomeTotal = 0;
    this.goalsAwayTotal = 0;
    this.penaltyShotHomeScored = 0;
    this.penaltyShotHomeMissed = 0;
    this.penaltyShotAwayScored = 0;
    this.penaltyShotAwayMissed = 0;
    this.winnerHome = 0;
    this.winnerAway = 0;
    this.winnerDraw = 0;
    this.playedET = [0, 0, 0, 0];
    this.playedPenalties = [0, 0, 0, 0];
    this.duration = 0;
    this.injuryTimeTotal = 0;
    this.injuryTimeHalvesTotal = 0;
    this.injuryTimeThirdsTotal = 0;
    this.injuryTimeQuartersTotal = 0;
    this.injuryTimeByHalvesTotal = [0, 0];
    this.injuryTimeByThirdsTotal = [0, 0, 0];
    this.injuryTimeByQuartersTotal = [0, 0, 0, 0];
    this.distanceTotal = 0;
    this.distanceAsRefereeTotal = 0;
    this.distanceAsAssistantTotal = 0;
    this.distanceHalvesTotal = 0;
    this.distanceThirdsTotal = 0;
    this.distanceQuartersTotal = 0;
    this.distanceByHalvesTotal = [0, 0];
    this.distanceByThirdsTotal = [0, 0, 0];
    this.distanceByQuartersTotal = [0, 0, 0, 0];
    this.sprintsMediumAndHigh = 0;
    this.sprintsTotal = [0, 0, 0];
    this.sprintsHalvesTotal = [0, 0, 0];
    this.sprintsThirdsTotal = [0, 0, 0];
    this.sprintsQuartersTotal = [0, 0, 0];
    this.sprintsDistanceTotal = [0, 0, 0];
    this.sprintsDistanceHalvesTotal = [0, 0, 0];
    this.sprintsDistanceThirdsTotal = [0, 0, 0];
    this.sprintsDistanceQuartersTotal = [0, 0, 0];
    this.sprintsByHalvesTotal = [
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByThirdsTotal = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByQuartersTotal = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByHalvesDistanceTotal = [
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByThirdsDistanceTotal = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByQuartersDistanceTotal = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.speedCategoryDurations = [0, 0, 0, 0, 0, 0];
    this.speedCategoryDistances = [0, 0, 0, 0, 0, 0];
    this.feesTotal = 0;
    this.expensesTotal = 0;
    this.earningsTotal = 0;
    this.earningsAsRefereeTotal = 0;
    this.earningsAsAssistantTotal = 0;
    this.earningsAsFourthOfficialTotal = 0;
    this.earningsAsObserverTotal = 0;
    this.heartRateAverage = 0;
    this.heartRateMax = 0;
    this.heartRate15Mins = [];
    this.heartRateZoneDuration = [0, 0, 0, 0, 0];
    this.heartRateZone4And5 = 0;
    this.redCardTotal = 0;
    this.yellowCardTotal = 0;
    this.yellowCardByTime = [];
    this.yellowCardByHomePlayer = {};
    this.yellowCardByAwayPlayer = {};
    this.redCardByHomePlayer = {};
    this.redCardByAwayPlayer = {};
    this.yellowCardHomeTotal = 0;
    this.yellowCardAwayTotal = 0;
    this.redCardHomeTotal = 0;
    this.redCardAwayTotal = 0;
    this.yellowCardPositions = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.redCardPositions = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.yellowCardCodes = {};
    this.redCardCodes = {};
    this.sinBinsTotal = 0;
    this.sinBinsMinutesSpent = 0;
    this.sinBinsByTime = [];
    this.sinBinsGoalPlayerTeam = 0;
    this.sinBinsGoalOppositeTeam = 0;
    this.gpsCenterPoint = { time: 0, latitude: 0, longitude: 0 };
    this.gpsAvailable = [0, 0, 0, 0];
    this.heartRateAvailable = [0, 0, 0, 0];
    this.gpsProcessed = 0;
    this.heartRateProcessed = 0;
  }
}
class AggregatedStats extends Stats {
  constructor() {
    super();
    this.matchesTotal = 0;
    this.matchesAveragePerMonth = 0;
    this.matchesHalves = 0;
    this.matchesThirds = 0;
    this.matchesQuarters = 0;
    this.matchesAsReferee = 0;
    this.matchesAsAssistant = 0;
    this.matchesAsObserver = 0;
    this.matchesAsFourthOfficial = 0;
    this.matchesAsRefereeWithGPS = 0;
    this.matchesAsAssistantWithGPS = 0;
    this.matchesWithEarnings = 0;
    this.matchesWithSinBins = 0;
    this.matchesByMonth = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.redCardAverage = 0;
    this.yellowCardAverage = 0;
    this.cardsAverage = 0;
    this.sinBinsAverage = 0;
    this.winnerHomePercentage = 0;
    this.winnerAwayPercentage = 0;
    this.winnerDrawPercentage = 0;
    this.distanceAverage = 0;
    this.distanceAsRefereeAverage = 0;
    this.distanceAsAssistantAverage = 0;
    this.distanceHalvesAverage = 0;
    this.distanceThirdsAverage = 0;
    this.distanceQuartersAverage = 0;
    this.distanceByHalvesAverage = [0, 0];
    this.distanceByThirdsAverage = [0, 0, 0];
    this.distanceByQuartersAverage = [0, 0, 0, 0];
    this.goalsAverage = 0;
    this.feesAverage = 0;
    this.expensesAverage = 0;
    this.dateOldestMatch = Infinity;
    this.dateNewestMatch = 0;
    this.heartRateMaxTotal = 0;
    this.heartRateAverageTotal = 0;
    this.heartRateZoneDurationAverage = [0, 0, 0, 0, 0];
    this.injuryTimeAverage = 0;
    this.injuryTimeHalvesAverage = 0;
    this.injuryTimeThirdsAverage = 0;
    this.injuryTimeQuartersAverage = 0;
    this.injuryTimeByHalvesAverage = [0, 0];
    this.injuryTimeByThirdsAverage = [0, 0, 0];
    this.injuryTimeByQuartersAverage = [0, 0, 0, 0];
    this.sprintsByHalvesAverage = [
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByThirdsAverage = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByQuartersAverage = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByHalvesDistanceAverage = [
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByThirdsDistanceAverage = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsByQuartersDistanceAverage = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0]
    ];
    this.sprintsTotalAverage = [0, 0, 0];
    this.sprintsDistanceTotalAverage = [0, 0, 0];
    this.yellowCardPositionsPercentage = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.redCardPositionsPercentage = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.gpsCenterPoints = [];
  }
}
var GoalType = /* @__PURE__ */ ((GoalType2) => {
  GoalType2["normal"] = "normal";
  GoalType2["ownGoal"] = "ownGoal";
  GoalType2["penalty"] = "penalty";
  GoalType2["freeKick"] = "freeKick";
  return GoalType2;
})(GoalType || {});
var HalfBarState = /* @__PURE__ */ ((HalfBarState2) => {
  HalfBarState2["hidden"] = "hidden";
  HalfBarState2["visible"] = "visible";
  return HalfBarState2;
})(HalfBarState || {});
var FitnessDataType = /* @__PURE__ */ ((FitnessDataType2) => {
  FitnessDataType2["GPS"] = "GPS";
  FitnessDataType2["HeartRate"] = "HEART_RATE";
  return FitnessDataType2;
})(FitnessDataType || {});
var PlayerPenaltyStatus = /* @__PURE__ */ ((PlayerPenaltyStatus2) => {
  PlayerPenaltyStatus2["goal"] = "goal";
  PlayerPenaltyStatus2["noGoal"] = "noGoal";
  PlayerPenaltyStatus2["none"] = "none";
  return PlayerPenaltyStatus2;
})(PlayerPenaltyStatus || {});
var PlayerPickerMode = /* @__PURE__ */ ((PlayerPickerMode2) => {
  PlayerPickerMode2["yellowCard"] = "yellowCard";
  PlayerPickerMode2["redCard"] = "redCard";
  PlayerPickerMode2["goal"] = "goal";
  PlayerPickerMode2["penaltyShot"] = "penaltyShot";
  PlayerPickerMode2["substitute"] = "substitute";
  return PlayerPickerMode2;
})(PlayerPickerMode || {});
var ReasonType = /* @__PURE__ */ ((ReasonType2) => {
  ReasonType2["yellow"] = "yellow";
  ReasonType2["red"] = "red";
  return ReasonType2;
})(ReasonType || {});
var SelectedTeam = /* @__PURE__ */ ((SelectedTeam2) => {
  SelectedTeam2["none"] = "none";
  SelectedTeam2["home"] = "home";
  SelectedTeam2["away"] = "away";
  return SelectedTeam2;
})(SelectedTeam || {});
var OfficialRole = /* @__PURE__ */ ((OfficialRole2) => {
  OfficialRole2["referee"] = "referee";
  OfficialRole2["assistant"] = "assistant";
  OfficialRole2["fourthOfficial"] = "fourthOfficial";
  OfficialRole2["observer"] = "observer";
  return OfficialRole2;
})(OfficialRole || {});
var PeriodNames = /* @__PURE__ */ ((PeriodNames2) => {
  PeriodNames2["firstPeriod"] = "1st Period";
  PeriodNames2["firstHalf"] = "First Half";
  PeriodNames2["secondHalf"] = "Second Half";
  PeriodNames2["firstThird"] = "1st Third";
  PeriodNames2["secondThird"] = "2nd Third";
  PeriodNames2["thirdThird"] = "3rd Third";
  PeriodNames2["firstQuarter"] = "1st Quarter";
  PeriodNames2["secondQuarter"] = "2nd Quarter";
  PeriodNames2["thirdQuarter"] = "3rd Quarter";
  PeriodNames2["fourthQuarter"] = "4th Quarter";
  PeriodNames2["halfTime"] = "Half Time";
  PeriodNames2["firstBreak"] = "1st Break";
  PeriodNames2["secondBreak"] = "2nd Break";
  PeriodNames2["thirdBreak"] = "3rd Break";
  PeriodNames2["et1"] = "ET1";
  PeriodNames2["et2"] = "ET2";
  return PeriodNames2;
})(PeriodNames || {});
var TeamOfficialRoles = /* @__PURE__ */ ((TeamOfficialRoles2) => {
  TeamOfficialRoles2["manager"] = "manager";
  TeamOfficialRoles2["assistant"] = "assistantManager";
  TeamOfficialRoles2["coach"] = "coach";
  TeamOfficialRoles2["physio"] = "physio";
  TeamOfficialRoles2["goalKeeperCoach"] = "goalKeeperCoach";
  TeamOfficialRoles2["fitnessCoach"] = "fitnessCoach";
  TeamOfficialRoles2["doctor"] = "doctor";
  TeamOfficialRoles2["kitManager"] = "kitManager";
  TeamOfficialRoles2["assistantKitManager"] = "assistantKitManager";
  TeamOfficialRoles2["teamSecretary"] = "teamSecretary";
  TeamOfficialRoles2["analyst"] = "analyst";
  TeamOfficialRoles2["other"] = "other";
  return TeamOfficialRoles2;
})(TeamOfficialRoles || {});
var SpeedCategoryNames = /* @__PURE__ */ ((SpeedCategoryNames2) => {
  SpeedCategoryNames2["stand"] = "stand";
  SpeedCategoryNames2["walk"] = "walk";
  SpeedCategoryNames2["jog"] = "jog";
  SpeedCategoryNames2["run"] = "run";
  SpeedCategoryNames2["fastRun"] = "fastRun";
  SpeedCategoryNames2["sprint"] = "sprint";
  return SpeedCategoryNames2;
})(SpeedCategoryNames || {});
var IntegrationPartner = /* @__PURE__ */ ((IntegrationPartner2) => {
  IntegrationPartner2["dbu"] = "DBU";
  IntegrationPartner2["stack"] = "stackSports";
  IntegrationPartner2["refassist"] = "refAssist";
  IntegrationPartner2["assignr"] = "assignr";
  IntegrationPartner2["knvb"] = "KNVB";
  IntegrationPartner2["refinsight"] = "refInsight";
  IntegrationPartner2["soccerRefManager"] = "soccerRefManager";
  return IntegrationPartner2;
})(IntegrationPartner || {});
var StoreReceiptPlatform = /* @__PURE__ */ ((StoreReceiptPlatform2) => {
  StoreReceiptPlatform2["APPLE_APPSTORE"] = "ios-appstore";
  StoreReceiptPlatform2["GOOGLE_PLAY"] = "android-playstore";
  return StoreReceiptPlatform2;
})(StoreReceiptPlatform || {});
class TeamPackDivision {
  constructor(name, division) {
    this.name = name;
    this.division = division;
  }
  static fromJSON(json) {
    return new TeamPackDivision(json.name, json.division);
  }
}
class TeamPackDivisionDetail {
  constructor(fullName, shortName, step) {
    this.fullName = fullName;
    this.shortName = shortName;
    this.step = step;
  }
  static fromJSON(json) {
    return new TeamPackDivisionDetail(
      json.fullName,
      json.shortName,
      json.step
    );
  }
}
class TeamPackDivisionTeamName {
  constructor(fullName) {
    this.fullName = fullName;
  }
  static fromJSON(json) {
    return new TeamPackDivisionTeamName(json.fullName);
  }
}
class TeamPackDivisionTeam {
  constructor(name, team) {
    this.name = name;
    this.team = team;
  }
  static fromJSON(json) {
    return new TeamPackDivisionTeam(
      json.name,
      json.team.map((t) => TeamPackDivisionTeamName.fromJSON(t))
    );
  }
}
class TeamPackTeamPlayers {
  constructor(name, players) {
    this.name = name;
    this.players = players;
  }
  static fromJSON(json) {
    return new TeamPackTeamPlayers(json.name, json.players);
  }
}
class TeamPackData {
  constructor(name, description, displayName, autoDownload, divisions, players, teams, allowCountry, allowNationalFA, lastModified) {
    this.name = name;
    this.description = description;
    this.displayName = displayName;
    this.autoDownload = autoDownload;
    this.divisions = divisions;
    this.players = players;
    this.teams = teams;
    this.allowCountry = allowCountry;
    this.allowNationalFA = allowNationalFA;
    this.lastModified = lastModified;
  }
  static fromJSON(json) {
    return new TeamPackData(
      json.name,
      json.description,
      json.displayName,
      json.autoDownload,
      json.divisions,
      json.players,
      json.teams,
      json.allowCountry,
      json.allowNationalFA,
      json.lastModified
    );
  }
}
class ClosedOpenDate {
  constructor(id, startDate, endDate, period, reason) {
    this.id = id;
    this.startDate = startDate;
    this.endDate = endDate;
    this.period = period;
    this.reason = reason;
  }
  static fromJSON(json) {
    return new ClosedOpenDate(
      json.id,
      json.startDate,
      json.endDate,
      json.period,
      json.reason
    );
  }
}
const REFSIX_AVAILABILITY_PREFIX = "r6_";
const REFSIX_OPEN_DATE_PREFIX = REFSIX_AVAILABILITY_PREFIX + "od_";
const REFSIX_CLOSED_DATE_PREFIX = REFSIX_AVAILABILITY_PREFIX + "cd_";
class RefAssistAvailability {
  // only if isFullDay is false
  constructor(refAssistId, partnerId, timeZoneId, timeZoneName, day, isFullDay, sportDisciplineId, comments, holidayType, start, end) {
    this.refAssistId = refAssistId;
    this.partnerId = partnerId;
    this.timeZoneId = timeZoneId;
    this.timeZoneName = timeZoneName;
    this.day = day;
    this.isFullDay = isFullDay;
    this.sportDisciplineId = sportDisciplineId;
    this.comments = comments;
    this.holidayType = holidayType;
    this.start = start;
    this.end = end;
  }
  static fromJSON(json) {
    return new RefAssistAvailability(
      json.refAssistId,
      json.partnerId,
      json.timeZoneId,
      json.timeZoneName,
      json.day,
      json.isFullDay,
      json.sportDisciplineId,
      json.comments,
      json.holidayType,
      json.start,
      json.end
    );
  }
}
class RefAssistFile {
  constructor(fileKey, size, isDirectory, lastModified) {
    this.fileKey = fileKey;
    this.size = size;
    this.isDirectory = isDirectory;
    this.lastModified = lastModified;
  }
  static fromJSON(json) {
    return new RefAssistFile(
      json.filekey,
      json.size,
      json.isDirectory,
      json.lastModified
    );
  }
}
var AvailabilityType = /* @__PURE__ */ ((AvailabilityType2) => {
  AvailabilityType2["openDate"] = "openDate";
  AvailabilityType2["closedDate"] = "closedDate";
  return AvailabilityType2;
})(AvailabilityType || {});
var RefAssistAlertType = /* @__PURE__ */ ((RefAssistAlertType2) => {
  RefAssistAlertType2["UserAlert"] = "UserAlert";
  RefAssistAlertType2["AdministratorAlert"] = "AdministratorAlert";
  RefAssistAlertType2["InconsistentRefereeAssignment"] = "InconsistentRefereeAssignment";
  RefAssistAlertType2["InconsistentObserverAssignment"] = "InconsistentObserverAssignment";
  RefAssistAlertType2["BackupNotification"] = "BackupNotification";
  RefAssistAlertType2["AutoCancellation"] = "AutoCancellation";
  RefAssistAlertType2["IncreasedTransportDistance"] = "IncreasedTransportDistance";
  RefAssistAlertType2["CoachingAdviceNotification"] = "CoachingAdviceNotification";
  RefAssistAlertType2["InconsistentInstructorAssignment"] = "InconsistentInstructorAssignment";
  RefAssistAlertType2["AssignmentRejected"] = "AssignmentRejected";
  RefAssistAlertType2["AssignmentAccepted"] = "AssignmentAccepted";
  RefAssistAlertType2["InconsistentCoachAssignment"] = "InconsistentCoachAssignment";
  RefAssistAlertType2["AppointmentPublishedNotification"] = "AppointmentPublishedNotification";
  RefAssistAlertType2["AppointmentCancellationNotification"] = "AppointmentCancellationNotification";
  RefAssistAlertType2["CoachAssignmentPublishedNotification"] = "CoachAssignmentPublishedNotification";
  RefAssistAlertType2["CoachAssignmentCancelledNotification"] = "CoachAssignmentCancelledNotification";
  RefAssistAlertType2["ObservationPublishedNotification"] = "ObservationPublishedNotification";
  RefAssistAlertType2["ObservationCancellationNotification"] = "ObservationCancellationNotification";
  RefAssistAlertType2["RefereeChangedForObservation"] = "RefereeChangedForObservation";
  RefAssistAlertType2["RefereeRemovedForObservation"] = "RefereeRemovedForObservation";
  RefAssistAlertType2["ObserverReportRejection"] = "ObserverReportRejection";
  RefAssistAlertType2["DocumentAvailableForRefereeNotification"] = "DocumentAvailableForRefereeNotification";
  RefAssistAlertType2["MatchStartDateChanged"] = "MatchStartDateChanged";
  RefAssistAlertType2["MatchVenueChanged"] = "MatchVenueChanged";
  RefAssistAlertType2["MatchTeamsChanged"] = "MatchTeamsChanged";
  RefAssistAlertType2["NewUserCreated"] = "NewUserCreated";
  RefAssistAlertType2["UserRolesAuthorisationsUpdated"] = "UserRolesAuthorisationsUpdated";
  RefAssistAlertType2["NewCourseSubmit"] = "NewCourseSubmit";
  RefAssistAlertType2["CourseStatusChanged"] = "CourseStatusChanged";
  RefAssistAlertType2["InstructorAppointmentPublishedNotification"] = "InstructorAppointmentPublishedNotification";
  RefAssistAlertType2["InstructorAppointmentCancelledNotification"] = "InstructorAppointmentCancelledNotification";
  RefAssistAlertType2["CourseDateChanged"] = "CourseDateChanged";
  RefAssistAlertType2["CourseCancellationRequest"] = "CourseCancellationRequest";
  RefAssistAlertType2["LeaveRequestRejected"] = "LeaveRequestRejected";
  RefAssistAlertType2["NewLeavesToBeApproved"] = "NewLeavesToBeApproved";
  RefAssistAlertType2["CourseInformationReminder"] = "CourseInformationReminder";
  RefAssistAlertType2["CheckListOverDueReminder"] = "CheckListOverDueReminder";
  RefAssistAlertType2["NewCourseAttachment"] = "NewCourseAttachment";
  RefAssistAlertType2["ExpenseRejected"] = "ExpenseRejected";
  RefAssistAlertType2["NoteOfExpenses"] = "NoteOfExpenses";
  RefAssistAlertType2["NewFileAvailable"] = "NewFileAvailable";
  RefAssistAlertType2["FormComponentValueNotification"] = "FormComponentValueNotification";
  RefAssistAlertType2["NewCourseInvitation"] = "NewCourseInvitation";
  RefAssistAlertType2["NewCourseAssignment"] = "NewCourseAssignment";
  RefAssistAlertType2["CourseAssignmentCancelled"] = "CourseAssignmentCancelled";
  RefAssistAlertType2["CourseCancelled"] = "CourseCancelled";
  RefAssistAlertType2["CourseInfoChanged"] = "CourseInfoChanged";
  RefAssistAlertType2["ReminderForAwardVoting"] = "ReminderForAwardVoting";
  RefAssistAlertType2["ReminderForMatchReport"] = "ReminderForMatchReport";
  return RefAssistAlertType2;
})(RefAssistAlertType || {});
class Profile {
}
class AuthTokenInfo {
  static fromJSON(json) {
    let authTokenInfo = new AuthTokenInfo();
    authTokenInfo.token = json.token;
    authTokenInfo.creation = json.creation;
    authTokenInfo.expiry = json.expiry;
    return authTokenInfo;
  }
}
class Template {
  constructor(name, config, _id) {
    this.name = name;
    this.config = config;
    this._id = _id;
  }
  static fromJSON(json) {
    let config = TemplateConfig.fromJSON(json.config);
    return new Template(json.name, config, json._id);
  }
  static fromMatch(name, match, _id, _rev) {
    let config = TemplateConfig.fromJSON(match);
    return new Template(name, config, _id);
  }
}
class Templates {
  constructor(templates, defaultTemplate, rev) {
    this._id = "r6_fixtureTemplates";
    this._rev = rev;
    this.templates = templates;
    this.defaultTemplate = defaultTemplate;
  }
  static fromJSON(json) {
    let templateList = [];
    if (json.templates && json.templates.length) {
      templateList = json.templates.map(function(templateJson) {
        return Template.fromJSON(templateJson);
      });
    }
    return new Templates(templateList, json.defaultTemplate, json._rev);
  }
}
class UpcomingMatches {
  // timestamp in millis
  constructor(matches, lastUpdated) {
    this.matches = matches;
    this.lastUpdated = lastUpdated;
  }
}
class MatchDownloadResponse {
  constructor(matches, subscriptionStatus) {
    this.matches = matches;
    this.subscriptionStatus = subscriptionStatus;
  }
}
class SubscriptionStatus {
  constructor(expiry, features, username) {
    this.expiry = expiry;
    this.features = features;
    this.username = username;
  }
}
class Device {
  constructor(model, watchAppVersion, os, matchCrashed, Permissions2, powerManagement) {
    this.model = model;
    this.watchAppVersion = watchAppVersion;
    this.os = os;
    this.matchCrashed = matchCrashed;
    this.permissions = Permissions2;
    this.powerManagement = powerManagement;
  }
}
class OS {
  constructor(name, version) {
    this.name = name;
    this.version = version;
  }
}
const CUSTOM_MISCONDUCT_CODE_ID = "custom";
class MisconductCodes {
  constructor(id, name, codes, locale, systemB) {
    this.id = id;
    this.name = name;
    this.locale = locale;
    this.systemB = systemB;
    this.codes = codes;
  }
  static fromJSON(json) {
    let codes = [];
    if (json.codes) {
      codes = json.codes.map(function(code) {
        return Reason.fromJSON(code);
      });
    }
    return new MisconductCodes(
      json.id,
      json.name,
      codes,
      json.locale,
      json.systemB
    );
  }
  static defaultCodes() {
    const yellow = new Reason(
      "Y1",
      "Yellow",
      ReasonType.yellow,
      false,
      false,
      void 0,
      false
    );
    const red = new Reason(
      "R1",
      "Red",
      ReasonType.red,
      true,
      false,
      void 0,
      false
    );
    const teamYellow = new Reason(
      "TY1",
      "Yellow",
      ReasonType.yellow,
      false,
      false,
      void 0,
      true
    );
    const teamRed = new Reason(
      "TR1",
      "Red",
      ReasonType.red,
      true,
      false,
      void 0,
      true
    );
    return new MisconductCodes(
      CUSTOM_MISCONDUCT_CODE_ID,
      "Custom",
      [yellow, red, teamYellow, teamRed],
      "en",
      void 0
    );
  }
}
class ConfigPhone {
  // disable half-end vibrations and alerts on watch
  //     }
  // }
  constructor(misconductCodes, disableWebPairing, showTimeOfDayOnKO, disableGPS, playerNumberPicker, showGoalOptions, hide13, recordRefereeRoles, layout, recordGoalScorers, automaticallyBeginHalfTime, elapsedHalfTimerFromZero, useImperial, use12HourTime, watchFace, misconductCode, customCodes, seasonDate, pairedWatch, watches, saveFixturesCalendar, teamPacks, screenAlwaysOn, disableTimerAlerts) {
    this.disableWebPairing = false;
    this.misconductCodes = misconductCodes;
    this.disableWebPairing = disableWebPairing;
    this.showTimeOfDayOnKO = showTimeOfDayOnKO;
    this.disableGPS = disableGPS;
    this.playerNumberPicker = playerNumberPicker;
    this.showGoalOptions = showGoalOptions;
    this.hide13 = hide13;
    this.recordRefereeRoles = recordRefereeRoles;
    this.layout = layout;
    this.recordGoalScorers = recordGoalScorers;
    this.automaticallyBeginHalfTime = automaticallyBeginHalfTime;
    this.elapsedHalfTimerFromZero = elapsedHalfTimerFromZero;
    this.useImperial = useImperial;
    this.use12HourTime = use12HourTime;
    this.watchFace = watchFace;
    this.misconductCode = misconductCode;
    this.customCodes = customCodes;
    this.seasonDate = seasonDate;
    this.pairedWatch = pairedWatch;
    this.watches = watches;
    this.saveFixturesCalendar = saveFixturesCalendar;
    this.teamPacks = teamPacks;
    this.screenAlwaysOn = screenAlwaysOn;
    this.disableTimerAlerts = disableTimerAlerts;
  }
  static fromJSON(json) {
    let misconductCodes = [];
    if (json.misconductCodes && json.misconductCodes.length) {
      json.misconductCodes.map(function(jsonReason) {
        return Reason.fromJSON(jsonReason);
      });
    }
    let settings = new ConfigPhone(
      misconductCodes,
      json.disableWebPairing,
      json.showTimeOfDayOnKO,
      json.disableGPS,
      json.playerNumberPicker,
      json.showGoalOptions,
      json.hide13,
      json.recordRefereeRoles,
      TimerLayout.fromJSON(json.layout),
      json.recordGoalScorers,
      json.automaticallyBeginHalfTime,
      json.elapsedHalfTimerFromZero,
      json.useImperial,
      json.use12HourTime,
      json.watchFace,
      json.misconductCode,
      [MisconductCodes.fromJSON(json.customCodes)],
      json.seasonDate,
      json.pairedWatch,
      json.watches,
      json.saveFixturesCalendar,
      json.teamPacks,
      json.screenAlwaysOn,
      json.disableTimerAlerts
    );
    return settings;
  }
  static defaultConfig() {
    const seasonDate = new Date(
      (/* @__PURE__ */ new Date()).getFullYear() - 1,
      8,
      1
    ).toISOString();
    const layout = new TimerLayout(
      TimerType.countdown,
      void 0,
      TimerType.elapsed,
      TimerType.additional
    );
    return new ConfigPhone(
      [],
      false,
      false,
      // showTimeOfDayOnKO - default OFF
      false,
      false,
      false,
      false,
      false,
      layout,
      false,
      true,
      false,
      false,
      false,
      WatchFace.bigTimers,
      "fifa",
      [MisconductCodes.defaultCodes()],
      seasonDate,
      "",
      [],
      false,
      [],
      true,
      false
    );
  }
}
class ConfigurationResponse {
  constructor(customCodes, settings, templateDoc) {
    this.customCodes = customCodes;
    this.settings = settings;
    this.templateDoc = templateDoc;
  }
  static fromJSON(json) {
    return new ConfigurationResponse(
      MisconductCodes.fromJSON(json.customCodes),
      ConfigPhone.fromJSON(json.settings),
      Templates.fromJSON(json.templateDoc)
    );
  }
}
class DeviceSettings {
  constructor(disableGPS) {
    this.disableGPS = disableGPS;
  }
  static fromJSON(json) {
    return new DeviceSettings(json.disableGPS);
  }
}
class SubscriptionDetails {
  constructor(subscriptions, features, signUpTimestamp, beenPro, beenBasic) {
    this.subscriptions = [];
    this.features = [];
    this.beenPro = false;
    this.beenBasic = false;
    this.subscriptions = subscriptions;
    this.features = features;
    this.signUpTimestamp = signUpTimestamp;
    this.beenPro = beenPro;
    this.beenBasic = beenBasic;
  }
}
class SubscriptionItems {
  constructor(validUntil, provider, id, planDescription, planName, status, updatedAt, rawData, planFeatures) {
    this.validUntil = validUntil;
    this.provider = provider;
    this.id = id;
    this.planDescription = planDescription;
    this.planName = planName;
    this.status = status;
    this.updatedAt = updatedAt;
    this.rawData = rawData;
    this.planFeatures = planFeatures;
  }
  static fromJSON(json) {
    return new SubscriptionItems(
      json.validUntil,
      json.provider,
      json.id,
      json.planDescription,
      json.planName,
      json.status,
      json.updatedAt,
      json.rawData,
      json.planFeatures
    );
  }
}
var MatchStatusExternal = /* @__PURE__ */ ((MatchStatusExternal2) => {
  MatchStatusExternal2["proposed"] = "proposed";
  MatchStatusExternal2["accepted"] = "accepted";
  MatchStatusExternal2["declined"] = "declined";
  MatchStatusExternal2["cancelled"] = "cancelled";
  MatchStatusExternal2["postponed"] = "postponed";
  MatchStatusExternal2["abandoned"] = "abandoned";
  MatchStatusExternal2["live"] = "live";
  MatchStatusExternal2["finished"] = "finished";
  MatchStatusExternal2["confirmed"] = "confirmed";
  return MatchStatusExternal2;
})(MatchStatusExternal || {});
var MessageType = /* @__PURE__ */ ((MessageType2) => {
  MessageType2["Alert"] = "alert";
  MessageType2["Message"] = "message";
  return MessageType2;
})(MessageType || {});
class Message {
  constructor(id, type, title, from, message, date, read, readDate, matchId, alertType) {
    this.id = id;
    this.type = type;
    this.title = title;
    this.message = message;
    this.date = date;
    this.read = read;
    this.readDate = readDate;
    this.matchId = matchId;
    this.alertType = alertType;
  }
  static fromJSON(json) {
    return new Message(
      json.id,
      json.type,
      json.title,
      json.from,
      json.message,
      json.date,
      json.read,
      json.readDate,
      json.matchId,
      json.alertType
    );
  }
}
class Permissions {
  constructor(gpsDisabled, gpsPermissionsEnabled, hrPermissionsEnabled) {
    this.gpsDisabled = gpsDisabled;
    this.gpsPermissionsEnabled = gpsPermissionsEnabled;
    this.hrPermissionsEnabled = hrPermissionsEnabled;
  }
}
class PowerManagement {
}
class MatchSummary {
  constructor(id, homeTeam, awayTeam, date, updateCount) {
    this.id = id;
    this.homeTeam = homeTeam;
    this.awayTeam = awayTeam;
    this.date = date;
    this.updateCount = updateCount;
  }
  static fromJSON(json) {
    return new MatchSummary(
      json.id,
      json.homeTeam,
      json.awayTeam,
      json.date,
      json.updateCount
    );
  }
}
export {
  AbandonEvent,
  AggregatedStats,
  AuthTokenInfo,
  AvailabilityType,
  CURRENT_STATS_VERSION,
  CUSTOM_MISCONDUCT_CODE_ID,
  CardStatus,
  ClosedOpenDate,
  Config,
  ConfigPhone,
  ConfigurationResponse,
  Device,
  DeviceSettings,
  EventName,
  FitnessDataType,
  FlashMessage,
  GoalEvent,
  GoalType,
  Half,
  HalfBarState,
  HalfEvent,
  IncidentEvent,
  InjuryTimeUpdate,
  Integration,
  IntegrationPartner,
  Match,
  MatchData,
  MatchDownloadResponse,
  MatchEvent,
  MatchOfficials,
  MatchPhone,
  MatchStatusExternal,
  MatchSummary,
  Message,
  MessageType,
  MisconductCodes,
  OS,
  OfficialRole,
  PenaltiesEvent,
  PenaltiesScore,
  PenaltyShotEvent,
  PeriodNames,
  Permissions,
  Player,
  PlayerCardStatus,
  PlayerOfTheMatch,
  PlayerPenaltyStatus,
  PlayerPickerMode,
  PowerManagement,
  Profile,
  REFSIX_AVAILABILITY_PREFIX,
  REFSIX_CLOSED_DATE_PREFIX,
  REFSIX_OPEN_DATE_PREFIX,
  Reason,
  ReasonType,
  RefAssistAlertType,
  RefAssistAvailability,
  RefAssistFile,
  SelectedTeam,
  SinBinSystem,
  SinBinTimer,
  SpeedCategoryNames,
  Stats,
  StoreReceiptPlatform,
  SubscriptionDetails,
  SubscriptionItems,
  SubscriptionStatus,
  SubstitutionEvent,
  Team,
  TeamBehavior,
  TeamOfficialRoles,
  TeamPackData,
  TeamPackDivision,
  TeamPackDivisionDetail,
  TeamPackDivisionTeam,
  TeamPackDivisionTeamName,
  TeamPackTeamPlayers,
  Template,
  TemplateConfig,
  Templates,
  TimerLayout,
  TimerType,
  Timings,
  UpcomingMatches,
  WatchFace
};
//# sourceMappingURL=refsix-js-models.esm.js.map
